import React from 'react';
import { useDropzone } from 'react-dropzone';

/**
 * File dropzone that supports both click-to-browse and drag-and-drop.
 * @param {string} [accept] - Comma-separated extensions, e.g. ".csv" or ".csv,.json"
 * @param {boolean} [disabled]
 * @param {(file: File) => void} onFileSelect - Called with the selected file (single file only).
 * @param {(message: string) => void} [onReject] - Called when the file is rejected (e.g. too large, wrong type).
 * @param {string} [tooLargeHint] - Extra sentence when file exceeds maxSize (e.g. point users to S3 upload).
 * @param {number} [maxSize] - Max file size in bytes.
 * @param {string} [inputId] - id for the hidden input (e.g. for clearing from parent).
 * @param {string} [label] - Optional label text above the zone.
 * @param {string} [className] - Extra class for the root container.
 */
function _formatMaxSize(bytes) {
  if (bytes == null || bytes <= 0) return '';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb >= 10 ? Math.round(gb) : gb.toFixed(1).replace(/\.0$/, '')} GB`;
  const mb = bytes / (1024 * 1024);
  return `${Math.round(mb)} MB`;
}

const FileDropzone = ({
  accept,
  disabled = false,
  onFileSelect,
  onReject,
  tooLargeHint,
  maxSize,
  inputId = 'file-dropzone-input',
  label,
  className = '',
}) => {
  const acceptMap = React.useMemo(() => {
    if (!accept || !accept.trim()) return undefined;
    const exts = accept.split(',').map((e) => e.trim().toLowerCase());
    const mimeByExt = {
      '.csv': ['text/csv', 'application/csv', 'text/plain'],
      '.json': ['application/json'],
    };
    const map = {};
    exts.forEach((ext) => {
      const normalized = ext.startsWith('.') ? ext : `.${ext}`;
      const mimes = mimeByExt[normalized] || ['application/octet-stream'];
      mimes.forEach((mime) => {
        if (!map[mime]) map[mime] = [];
        if (!map[mime].includes(normalized)) map[mime].push(normalized);
      });
    });
    return Object.keys(map).length ? map : undefined;
  }, [accept]);

  const onDrop = React.useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const errs = rejection.errors || [];
        const err = errs[0];
        let msg = 'Could not use this file.';
        if (err?.code === 'file-too-large' && maxSize != null) {
          const limit = _formatMaxSize(maxSize);
          msg = `This file is too large (maximum ${limit}).`;
          if (tooLargeHint) msg = `${msg} ${tooLargeHint}`;
        } else if (err?.code === 'file-invalid-type') {
          msg =
            err.message ||
            'File type is not allowed. Use one of the accepted extensions shown in the upload area.';
        } else if (err?.message) {
          msg = err.message;
        }
        if (onReject) {
          onReject(msg);
        } else {
          // eslint-disable-next-line no-console
          console.warn(msg);
        }
      }
      const file = acceptedFiles[0];
      if (file) onFileSelect(file);
    },
    [onFileSelect, onReject, maxSize, tooLargeHint]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptMap,
    maxSize: maxSize ?? undefined,
    multiple: false,
    disabled,
    noClick: false,
    noKeyboard: false,
  });

  return (
    <div className={className}>
      {label && (
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        {...getRootProps()}
        className={`
          flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors
          ${disabled ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60' : 'border-gray-300 bg-gray-50 hover:border-[#0062FB] hover:bg-blue-50/50'}
          ${isDragActive ? 'border-[#0062FB] bg-blue-50' : ''}
        `}
      >
        <input {...getInputProps({ id: inputId })} />
        <p className="text-sm text-gray-600">
          {isDragActive ? 'Drop the file here' : 'Drag and drop a file here, or click to browse'}
        </p>
      </div>
    </div>
  );
};

export default FileDropzone;
